import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of } from 'rxjs';
import * as CompanyGroupActions from './company-group.actions';
import { CompanyGroupService } from 'app/modules/masters/company-group/company-group.service';
import { CompanyGroup } from 'app/modules/masters/company-group/models/company-group.model';

@Injectable()
export class CompanyGroupEffects {
  constructor(
    private actions$: Actions,
    private companyGroupService: CompanyGroupService
  ) {}

  loadCompanyGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyGroupActions.loadCompanyGroups),
      mergeMap(() =>
        this.companyGroupService.getAll().pipe(
          map((companyGroups: CompanyGroup[]) =>
            CompanyGroupActions.loadCompanyGroupsSuccess({ companyGroups })
          ),
          catchError((error) =>
            of(CompanyGroupActions.companyGroupFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadCompanyGroupsPaginated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyGroupActions.loadCompanyGroupsPaginated),
      mergeMap(({ page, limit, sortBy, sortOrder, companyGroupStatus }) =>
        this.companyGroupService
          .getAllPaginated(page, limit, sortBy, sortOrder, companyGroupStatus)
          .pipe(
          map((response) =>
            CompanyGroupActions.loadCompanyGroupsPaginatedSuccess(response)
          ),
          catchError((error) =>
            of(CompanyGroupActions.companyGroupFailure({ error: error.message }))
          )
        )
      )
    )
  );

  deleteCompanyGroup$ = createEffect(() =>
  this.actions$.pipe(
    ofType(CompanyGroupActions.deleteCompanyGroup),
    mergeMap(({ id }) =>
      this.companyGroupService.delete(id).pipe(
        map(() => CompanyGroupActions.deleteCompanyGroupSuccess({ id })),
        catchError((error) =>
          of(CompanyGroupActions.companyGroupFailure({ error: error.message }))
        )
      )
    )
  )
);

createCompanyGroup$ = createEffect(() =>
  this.actions$.pipe(
    ofType(CompanyGroupActions.createCompanyGroup),
    mergeMap(({ companyGroup }) =>
      this.companyGroupService.create(companyGroup as CompanyGroup).pipe(
        // Reload using paginated API (matches backend + keeps total in sync)
        map(() =>
          CompanyGroupActions.loadCompanyGroupsPaginated({
            page: 1,
            limit: 10,
            sortBy: 'createdAt',
            sortOrder: 'desc',
          }),
        ),
        catchError((error) =>
          of(CompanyGroupActions.companyGroupFailure({ error: error.message }))
        )
      )
    )
  )
);


updateCompanyGroup$ = createEffect(() =>
  this.actions$.pipe(
    ofType(CompanyGroupActions.updateCompanyGroup),
    mergeMap(({ id, companyGroup }) =>
      this.companyGroupService.update(id, companyGroup as CompanyGroup).pipe(
        map((updatedCompanyGroup: CompanyGroup) => {
          // Dispatch both success action and reload action
          return CompanyGroupActions.updateCompanyGroupSuccess({ id, companyGroup: updatedCompanyGroup });
        }),
        catchError((error) =>
          of(CompanyGroupActions.companyGroupFailure({ error: error.message }))
        )
      )
    )
  )
);

// Reload company groups after successful update
reloadCompanyGroupsAfterUpdate$ = createEffect(() =>
  this.actions$.pipe(
    ofType(CompanyGroupActions.updateCompanyGroupSuccess),
    map(() =>
      CompanyGroupActions.loadCompanyGroupsPaginated({
        page: 1,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      }),
    )
  )
);



searchCompanyGroups$ = createEffect(() =>
  this.actions$.pipe(
    ofType(CompanyGroupActions.searchCompanyGroups),
    mergeMap(({ query, companyGroupStatus }) =>
      this.companyGroupService.search(query, companyGroupStatus).pipe(
        map((results) =>
          CompanyGroupActions.searchCompanyGroupsSuccess({ results })
        ),
        catchError((error) =>
          of(CompanyGroupActions.companyGroupFailure({ error: error.message }))
        )
      )
    )
  )
);


}

///import { CompanyGroupService } from '../../modules/company-group/company-group.service';